import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { clergyService } from '../services/clergy.service';
import { Clergy } from '../types';
import { ClergyTable, ClergyToolbar } from '../components/clergy/ClergyTable';
import { ClergyForm } from '../components/clergy/ClergyForm';

export default function ClergyPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [rankFilter, setRankFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingClergy, setEditingClergy] = useState<Clergy | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const {
    data: clergyData,
    isLoading,
  } = useQuery({
    queryKey: ['clergy', { page, limit, search, rankFilter }],
    queryFn: () => {
      const filters: Record<string, any> = { page, limit, search };
      if (rankFilter) filters.rank = rankFilter;
      return clergyService.getList(filters);
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Clergy, 'id' | 'createdAt' | 'updatedAt'>) => clergyService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clergy'] });
      setIsFormOpen(false);
      setEditingClergy(null);
      setNotification({ message: 'Священнослужитель добавлен', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при добавлении', type: 'error' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Clergy> }) => clergyService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clergy'] });
      setIsFormOpen(false);
      setEditingClergy(null);
      setNotification({ message: 'Данные обновлены', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при обновлении', type: 'error' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => clergyService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clergy'] });
      setNotification({ message: 'Запись удалена', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при удалении', type: 'error' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: number) => clergyService.toggleActive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clergy'] });
      setNotification({ message: 'Статус изменен', type: 'success' });
    },
    onError: () => {
      setNotification({ message: 'Ошибка при изменении статуса', type: 'error' });
    },
  });

  const filteredClergy = clergyData?.data || [];
  const total = clergyData?.pagination?.total || 0;

  const handleCreate = () => {
    setEditingClergy(null);
    setIsFormOpen(true);
  };

  const handleEdit = (clergy: Clergy) => {
    setEditingClergy(clergy);
    setIsFormOpen(true);
  };

  const handleSubmit = async (data: any) => {
    if (editingClergy) {
      await updateMutation.mutateAsync({ id: editingClergy.id, data });
    } else {
      await createMutation.mutateAsync(data);
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm('Вы уверены, что хотите удалить эту запись?')) {
      deleteMutation.mutate(id);
    }
  };

  const handleToggleActive = (id: number) => {
    toggleActiveMutation.mutate(id);
  };

  const handleCloseNotification = () => {
    setNotification(null);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>
          Духовенство
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Добавить
        </Button>
      </Box>

      <ClergyToolbar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        rankFilter={rankFilter}
        onRankFilterChange={(value) => {
          setRankFilter(value);
          setPage(1);
        }}
      />

      <ClergyTable
        clergy={filteredClergy}
        loading={isLoading}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onToggleActive={handleToggleActive}
      />

      <ClergyForm
        open={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        initialData={editingClergy as any}
        loading={createMutation.isPending || updateMutation.isPending}
        error={createMutation.error?.message || updateMutation.error?.message}
      />

      <Snackbar
        open={!!notification}
        autoHideDuration={3000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification?.type === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
          icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
